import {
  VENTANA_VENTAS_DIAS,
  urlWhatsappComisionManual,
} from '../../../core/monetizacion/vendedorNiveles.js';
import { evaluarBloqueoPublicar, progresoSiguienteNivel } from '../domain/vendedorNivelRules.js';
import { escapeHtml as esc } from '../../../core/ui/escapeHtml.js';
import { withBusyButton } from '../../../core/ui/withBusyButton.js';
import { openMercadoPagoAliasModal } from '../../../core/ui/mercadoPagoAliasModal.js';
import {
  iconBackPub,
  iconImageSmallPub,
  iconNavCompassPub,
  iconNavPlusPub,
  iconNavPersonPub,
  iconNavServiciosPub,
} from './publicarPresenter.js';
import { runCompletarVentaFlow } from './completarVentaUi.js';
import { isServiciosNavVisible } from '../../../core/config/mlvFeatureFlags.js';
import { mlvTrack } from '../../../core/telemetry/mlvTrack.js';
import { MLV_EVENTS } from '../../../core/telemetry/mlvEvents.js';

function sortByCreatedDesc(items) {
  return [...items].sort((a, b) => {
    const ta = a.creadoEn?.toMillis?.() ?? 0;
    const tb = b.creadoEn?.toMillis?.() ?? 0;
    return tb - ta;
  });
}

function formatoPrecioAr(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

function iconWalletMis() {
  return `<svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h13v1"/><path d="M17 12h.01"/></svg>`;
}
function iconBankMis() {
  return `<svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21h18"/><path d="M5 21V7.8L12 3l7 4.8V21"/><path d="M9 21v-4h6v4"/><path d="M9 10h2"/><path d="M13 10h2"/></svg>`;
}
function iconInfoMis() {
  return `<svg class="mt-0.5 h-4 w-4 shrink-0 text-brand-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path stroke-linecap="round" d="M12 16v-4M12 8h.01"/></svg>`;
}

export function createMisPublicacionesView({
  render,
  getFirebase,
  repo,
  backToHome,
  onOpenProfile,
  onMisNavPerfil,
  onIrAlCatalogo,
  onOpenServicios,
  showPublicarView,
}) {
  let mpCheckoutInFlight = false;
  let mpDeudaUnsub = null;
  const MP_CHECKOUT_BTN_LABEL = 'Mercado Pago';
  const MP_WIN_NAME = 'mlv_mp_checkout';

  return async function misPublicacionesView(uid, options = {}) {
    if (typeof mpDeudaUnsub === 'function') {
      mpDeudaUnsub();
      mpDeudaUnsub = null;
    }
    const { onBack } = options;
    let items = [];
    let profile = null;
    try {
      profile = await repo.loadUserProfile(uid);
      items = sortByCreatedDesc(await repo.listarMisPublicaciones(uid));
    } catch (err) {
      console.error(err);
      items = [];
    }
    const resumen = repo.resumenNivelVendedor(profile);
    const prog = progresoSiguienteNivel(resumen.ventasEnVentana);
    const bloqPub = evaluarBloqueoPublicar(
      profile?.comision_deuda_ars,
      profile?.saldo_favor_ars,
      profile?.comision_ventas_impagas_count
    );
    const deudaBruta = Number(resumen.deudaArs) || 0;
    const saldoFavor = Number(resumen.saldoFavorArs) || 0;
    const netPagar = Math.max(0, deudaBruta - saldoFavor);
    let balanceMontoFmt;
    let balanceLabel;
    let balanceColorClass;
    if (deudaBruta <= 0 && saldoFavor <= 0) {
      balanceMontoFmt = '0';
      balanceLabel = 'Al día';
      balanceColorClass = 'text-slate-600';
    } else if (deudaBruta <= 0) {
      balanceMontoFmt = saldoFavor.toLocaleString('es-AR', { maximumFractionDigits: 0 });
      balanceLabel = 'Saldo a favor';
      balanceColorClass = 'text-emerald-700';
    } else if (netPagar > 0) {
      balanceMontoFmt = netPagar.toLocaleString('es-AR', { maximumFractionDigits: 0 });
      balanceLabel = 'Deuda por ventas';
      balanceColorClass = 'text-red-700';
    } else {
      balanceMontoFmt = '0';
      balanceLabel = 'Deuda por ventas';
      balanceColorClass = 'text-emerald-700';
    }
    const impFmt = String(resumen.impagas ?? 0);
    const barPct = Math.min(100, Math.max(0, prog.barPct));
    const ventasVentanaFmt = String(resumen.ventasEnVentana ?? 0);
    const subBar =
      prog.siguienteUmbral != null
        ? `${prog.actual} / ${prog.siguienteUmbral} ventas hacia ${esc(prog.siguienteLabel)}`
        : 'Nivel máximo alcanzado en la ventana.';

    render(`
      <div class="min-h-screen bg-brand-surface mlv-bg-surface pb-28">
        <header class="sticky top-0 z-20 flex items-center gap-2 border-b border-slate-200/80 bg-brand-surface/95 px-3 py-3 backdrop-blur-sm">
          <button type="button" id="mis-back" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-200/60" aria-label="Volver">
            ${iconBackPub()}
          </button>
          <h1 class="mlv-screen-title min-w-0 flex-1 font-heading text-base font-bold tracking-tight text-brand-primary">Mis publicaciones</h1>
        </header>
        <main class="mx-auto max-w-lg px-4 pt-4">
          <section class="mb-4 rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-100">
            <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Tu progreso</p>
            <div class="mt-2 flex items-start justify-between gap-3">
              <h2 class="font-heading text-2xl font-bold leading-tight text-brand-primary">${esc(prog.labelActual)}</h2>
              <div class="shrink-0 text-right">
                <p class="text-xl font-bold tabular-nums text-slate-900">${esc(ventasVentanaFmt)}</p>
                <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Ventas</p>
                <p class="mt-0.5 text-[9px] text-slate-400">últ. ${VENTANA_VENTAS_DIAS} días</p>
              </div>
            </div>
            <div class="mt-4 h-3 w-full overflow-hidden rounded-full bg-brand-muted">
              <div class="h-full rounded-full bg-brand-primary transition-all" style="width:${barPct}%"></div>
            </div>
            <p class="mt-2 text-[11px] leading-relaxed text-slate-600">${esc(subBar)}</p>
            <div class="mt-5 grid grid-cols-2 gap-3">
              <div class="rounded-2xl bg-brand-muted/90 px-3 py-3 text-center ring-1 ring-brand-primary/10">
                <p class="text-lg font-bold tabular-nums ${balanceColorClass}">$ ${esc(balanceMontoFmt)}</p>
                <p class="mt-0.5 text-[11px] font-semibold text-slate-700">${esc(balanceLabel)}</p>
              </div>
              <div class="rounded-2xl bg-brand-muted/90 px-3 py-3 text-center ring-1 ring-brand-primary/10">
                <p class="text-lg font-bold tabular-nums text-slate-900">${esc(impFmt)}</p>
                <p class="mt-0.5 text-[11px] font-semibold text-slate-600">Ventas impagas</p>
              </div>
            </div>
            ${resumen.enTrialNivel5 ? `<div class="mt-4 flex gap-2 rounded-2xl bg-brand-muted px-3 py-2.5 text-[12px] leading-snug text-brand-primary">${iconInfoMis()}<span>Período de prueba: la comisión se calcula como <strong class="font-semibold">nivel 5</strong>.</span></div>` : ''}
            ${resumen.premioNivel5Etiqueta ? `<p class="mt-3 text-center text-[11px] font-semibold text-brand-primary">Premio Nivel 5 — gracias por seguir en el barrio.</p>` : ''}
            ${bloqPub.bloqueado ? `<p class="mt-3 rounded-2xl bg-amber-50 px-3 py-2.5 text-[11px] leading-relaxed text-amber-900 ring-1 ring-amber-200/80">${esc(bloqPub.motivo || 'Regularizá la comisión para publicar de nuevo.')}</p>` : ''}
            <div class="mt-5 space-y-3">
              <button type="button" id="mis-open-mp" class="flex w-full items-center justify-center rounded-2xl bg-brand-primary py-3.5 text-sm font-bold text-white shadow-md shadow-brand-primary/25 transition hover:brightness-105 active:scale-[0.99]">
                <span class="flex items-center justify-center gap-2">
                  <span class="mis-mp-ico" aria-hidden="true">${iconWalletMis()}</span>
                  <span id="mis-mp-btn-label">${esc(MP_CHECKOUT_BTN_LABEL)}</span>
                </span>
              </button>
              <a href="${urlWhatsappComisionManual()}" target="_blank" rel="noopener noreferrer" class="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-brand-primary bg-white py-3.5 text-sm font-bold text-brand-primary transition hover:bg-brand-muted/40">
                ${iconBankMis()}
                Depósito manual
              </a>
              <button type="button" id="mis-publicar-otro" class="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-muted py-3.5 text-sm font-bold text-brand-primary ring-1 ring-brand-primary/15 transition hover:bg-brand-primary/10">
                ${iconNavPlusPub()}
                Publicar otro producto
              </button>
            </div>
          </section>

          <h2 class="mb-3 font-heading text-lg font-bold text-brand-primary">Tus productos</h2>
          <ul id="mis-lista" class="space-y-4">
            ${
              items.length === 0
                ? '<li class="rounded-3xl border border-dashed border-slate-200/90 bg-white/90 py-12 text-center text-sm text-slate-500">Todavía no publicaste nada.</li>'
                : items
                    .map((it) => {
                      const img =
                        Array.isArray(it.fotos) && it.fotos[0]
                          ? `<img src="${esc(it.fotos[0])}" alt="" class="h-24 w-24 shrink-0 rounded-2xl bg-slate-100 object-cover ring-1 ring-slate-200/80" loading="lazy" />`
                          : `<div class="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-brand-muted/80 ring-1 ring-slate-200/60 text-slate-400">${iconImageSmallPub()}</div>`;
                      const precio = formatoPrecioAr(it.precio);
                      const est = String(it.estado || 'publicado').toUpperCase();
                      const vendidoBtn = `<button type="button" class="mis-vendido-btn w-full rounded-2xl bg-brand-primary py-3 text-sm font-bold text-white shadow-sm transition hover:brightness-105 active:scale-[0.99]" data-mis-vendido="${esc(it.id)}">¡Lo vendí!</button>`;
                      const bajaBtn = `<button type="button" class="mis-del-btn w-full rounded-2xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50" data-mis-del="${esc(it.id)}">Dar de baja</button>`;
                      return `
              <li class="relative overflow-hidden rounded-3xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-100">
                <span class="absolute right-3 top-3 rounded-full bg-brand-muted px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-primary ring-1 ring-brand-primary/15">${esc(est)}</span>
                <div class="flex gap-3 pr-20">
                  ${img}
                  <div class="flex min-w-0 flex-1 flex-col justify-center">
                    <p class="font-heading text-[15px] font-semibold leading-snug text-slate-900">${esc(it.titulo || 'Sin título')}</p>
                    <p class="mt-1.5 text-lg font-bold text-brand-tertiary">$ ${esc(precio)}</p>
                  </div>
                </div>
                <div class="mt-4 flex flex-col gap-2">
                  ${vendidoBtn}
                  ${bajaBtn}
                </div>
              </li>`;
                    })
                    .join('')
            }
          </ul>
          <p id="mis-msg" class="mt-4 min-h-[1.25rem] text-sm text-red-600"></p>
        </main>
        <nav class="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200/90 bg-white/95 px-2 pt-2 backdrop-blur-md" style="padding-bottom:max(0.75rem, env(safe-area-inset-bottom))">
          <div class="mx-auto flex max-w-lg items-center justify-around">
            <button type="button" id="mis-nav-explorar" class="mlv-nav-item flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-slate-500 hover:bg-slate-100">
              ${iconNavCompassPub()}
              <span class="text-[10px] font-medium">Explorar</span>
            </button>
            ${
              isServiciosNavVisible()
                ? `<button type="button" id="mis-nav-servicios" class="mlv-nav-item flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-slate-500 hover:bg-slate-100">
              ${iconNavServiciosPub()}
              <span class="text-[10px] font-medium">Servicios</span>
            </button>`
                : ''
            }
            <button type="button" id="mis-nav-vender" class="mlv-nav-active flex flex-1 flex-col items-center gap-1 rounded-xl bg-brand-primary/10 py-2 text-brand-primary">
              ${iconNavPlusPub()}
              <span class="text-[10px] font-semibold">Vender</span>
            </button>
            <button type="button" id="mis-nav-perfil" class="mlv-nav-item flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-slate-500 hover:bg-slate-100">
              ${iconNavPersonPub()}
              <span class="text-[10px] font-medium">Mi perfil</span>
            </button>
          </div>
        </nav>
      </div>
    `);
    const msgEl = document.getElementById('mis-msg');
    document.getElementById('mis-open-mp').onclick = async () => {
      const btn = document.getElementById('mis-open-mp');
      const mpLabel = document.getElementById('mis-mp-btn-label');
      if (mpCheckoutInFlight) return;
      mpCheckoutInFlight = true;
      if (msgEl) msgEl.textContent = '';
      let saleDelSitio = false;
      const deudaAntesPago = Math.max(
        0,
        Math.round(
          ((Number(profile?.comision_deuda_ars) || 0) - (Number(profile?.saldo_favor_ars) || 0)) * 100
        ) / 100
      );
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('cursor-wait', 'opacity-80');
      }
      if (mpLabel) mpLabel.textContent = 'Conectando con Mercado Pago…';
      try {
        const r = await repo.crearPagoComisionMercadoPago();
        if (r?.status === 'settled_by_credit') {
          if (msgEl) {
            msgEl.classList.remove('text-red-600');
            msgEl.classList.add('text-emerald-700');
            msgEl.textContent = 'La deuda quedó saldada usando saldo a favor. No hace falta pagar ahora.';
          }
          await misPublicacionesView(uid, options);
          return;
        }
        const url = String(r?.initPoint || r?.sandboxInitPoint || '').trim();
        if (!url) {
          openMercadoPagoAliasModal();
          if (msgEl) {
            msgEl.classList.remove('text-emerald-700');
            msgEl.classList.add('text-red-600');
            msgEl.textContent =
              'No pudimos abrir el checkout automático. Usá el alias del modal o revisá que Functions y MP_ACCESS_TOKEN estén configurados.';
          }
          return;
        }
        const win = window.open(url, MP_WIN_NAME, 'noopener,noreferrer');
        if (!win) {
          saleDelSitio = true;
          window.location.assign(url);
          return;
        }
        if (msgEl) {
          msgEl.classList.remove('text-red-600');
          msgEl.classList.add('text-emerald-700');
          const sandboxUrl = /sandbox\.mercadopago/i.test(url) || /mercadopago\.[a-z.]+\/sandbox/i.test(url);
          msgEl.textContent = sandboxUrl
            ? 'Abrimos Mercado Pago en otra pestaña (prueba). Pagá ahí; esta pantalla se actualiza cuando registremos el pago. Si te ofrece la app y falla, usá el navegador.'
            : 'Abrimos Mercado Pago en otra pestaña. Pagá ahí; cuando el pago se acredite, actualizamos esto solo. Si cerrás esta pestaña, volvé a Mis publicaciones.';
        }
        if (typeof repo.escucharBajadaDeudaComision === 'function' && deudaAntesPago > 0) {
          mpDeudaUnsub?.();
          mpDeudaUnsub = repo.escucharBajadaDeudaComision({
            uid,
            deudaReferenciaArs: deudaAntesPago,
            onBajada: async () => {
              mpDeudaUnsub = null;
              if (msgEl) {
                msgEl.classList.remove('text-red-600');
                msgEl.classList.add('text-emerald-700');
                msgEl.textContent = 'Listo: registramos el pago. Actualizando…';
              }
              await misPublicacionesView(uid, options);
            },
          });
        }
      } catch (err) {
        console.error(err);
        openMercadoPagoAliasModal();
        if (msgEl) {
          msgEl.classList.remove('text-emerald-700');
          msgEl.classList.add('text-red-600');
          msgEl.textContent =
            err?.message || 'No se pudo iniciar Mercado Pago. Te mostramos el alias para pagar por transferencia.';
        }
      } finally {
        if (!saleDelSitio) {
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            btn.classList.remove('cursor-wait', 'opacity-80');
          }
          if (mpLabel) mpLabel.textContent = MP_CHECKOUT_BTN_LABEL;
          mpCheckoutInFlight = false;
        }
      }
    };
    document.getElementById('mis-back').onclick = () => {
      if (typeof onBack === 'function') onBack();
      else backToHome();
    };
    document.getElementById('mis-publicar-otro').onclick = () => showPublicarView();
    document.getElementById('mis-nav-explorar').onclick = () => onIrAlCatalogo?.();
    document.getElementById('mis-nav-servicios')?.addEventListener('click', () => onOpenServicios?.());
    document.getElementById('mis-nav-perfil').onclick = () =>
      (typeof onMisNavPerfil === 'function' ? onMisNavPerfil() : onOpenProfile?.());
    document.getElementById('mis-nav-vender').onclick = () => showPublicarView();
    document.querySelectorAll('.mis-del-btn').forEach((btn) => {
      btn.onclick = async () => {
        const id = btn.getAttribute('data-mis-del');
        if (!id) return;
        if (
          !confirm(
            '¿Ocultar esta publicación del catálogo? No podrás recuperar las fotos desde la app.'
          )
        )
          return;
        if (msgEl) msgEl.textContent = '';
        await withBusyButton(btn, async () => {
          await repo.eliminarPublicacion(uid, id);
          mlvTrack(MLV_EVENTS.PUBLICACION_ELIMINADA);
          await misPublicacionesView(uid, options);
        }, { labelBusy: 'Dando de baja…' });
      };
    });

    async function completarVenta(id, sourceBtn) {
      if (!id) return;
      if (msgEl) msgEl.textContent = '';
      await runCompletarVentaFlow({
        getFirebase,
        repo,
        uid,
        productId: id,
        sourceBtn,
        trackOrigen: 'mis',
        onSuccess: () => misPublicacionesView(uid, options),
        onError: (err) => {
          console.error(err);
          if (msgEl) msgEl.textContent = err?.message || 'No se pudo marcar.';
        },
      });
    }

    document.querySelectorAll('.mis-vendido-btn').forEach((btn) => {
      btn.onclick = () => {
        const id = btn.getAttribute('data-mis-vendido');
        if (!id) return;
        void completarVenta(id, btn);
      };
    });
  };
}
